"use client";

import {
  ArrowUpRightIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  ScissorsIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { FC, type ReactNode, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import {
  type SocialProviderAccount,
  useSocialProviderAccounts,
} from "../../hooks/useSocialProviderAccounts";
import { useVideo } from "../../hooks/useVideo";
import { usePublicationStatusKind } from "../../hooks/usePublicationStatusKind";
import type { PublicationModel } from "../../types/models";
import { PublicationHelper } from "../../utils/PublicationHelper";
import { getPublicationErrorMessage } from "../../utils/publicationErrorMessages";
import Button from "../Button";
import AutoPostPublicationMark from "../autoPublish/AutoPostPublicationMark";
import PublicationCardCornerMenu from "../PublicationCardCornerMenu";
import {
  buildCalendarPostMenuItems,
  canOfferCalendarRetry,
  type CalendarPostMenuItemId,
  type CalendarPostMenuScope,
} from "../publicationFlow/calendarPostMenu";
import { selectPublicationStatusBadge } from "../publicationFlow/publicationStatusBadge";
import {
  selectCalendarNavigationPublications,
  stepCalendarPublication,
} from "../publicationFlow/calendarPostNavigation";
import Typography from "../Typography";
import VerticalStackIcon from "../VerticalStackIcon";
import CalendarPostPlayer from "./CalendarPostPlayer";
import CalendarPostStatusBadge from "./CalendarPostStatusBadge";
import CalendarPostNav from "./CalendarPostNav";

// Both calendars share this inspector. Navigation and actions share the top
// row; status sits above the caption. A failure explains itself below
// the media, with Retry in that one location.
// Hosts supply navigation and mutations so staged schedule edits stay local.
const MENU_ITEM_ICON: Record<CalendarPostMenuItemId, ReactNode> = {
  "edit-post": <PencilSquareIcon className="w-4 h-4" />,
  "edit-video": <ScissorsIcon className="w-4 h-4" />,
  "open-project": <VerticalStackIcon className="w-4 h-4" />,
  "open-schedule": <CalendarDaysIcon className="w-4 h-4" />,
  "view-live": <ArrowUpRightIcon className="w-4 h-4" />,
  "post-now": <PaperAirplaneIcon className="w-4 h-4" />,
  delete: <TrashIcon className="w-4 h-4" />,
};

export type CalendarPostActions = {
  /**
   * The post's SCHEDULE. On the global calendar that is a route to
   * `/schedule?…&mode=edit`; inside the scheduler it is already open, so the
   * host closes the inspector and jumps to the List tab instead.
   */
  onEditPost: (publication: PublicationModel, folderId: string | null) => void;
  /** Name the host's destination when the post is published or in flight. */
  openScheduleLabel?: CalendarPostMenuScope["openScheduleLabel"];
  /**
   * The SHORT, in the editor — a separate workspace, so both hosts open it in
   * a new tab (`editorHrefForVideo`) and neither routes its own tab away: the
   * calendar keeps its month, and the scheduler keeps its unsaved plan.
   */
  onEditVideo: (video: VideoModel) => void;
  /** The parent project's shorts, opened in a new tab by both hosts. */
  onOpenProject: (folderId: string) => void;
  /** Open the owning project’s Auto post controls without leaving the calendar. */
  onManageAutoPost?: (folderId: string) => void;
  /** The published post's numbers. Same reason as `onEditVideo` for the
   *  scheduler: the host, not this file, decides same-tab or new-tab. */
  onViewAnalytics: (publication: PublicationModel) => void;
  onPostNow: (
    video: VideoModel,
    publication: PublicationModel,
    account: SocialProviderAccount,
  ) => void;
  /** The account is null when it is gone — deletion only needs a label. */
  onDelete: (
    publication: PublicationModel,
    account: SocialProviderAccount | null,
  ) => void;
  /**
   * How the bin item lands — drives its menu label. Inside `/schedule`
   * removal is staged until Save ("Remove from schedule"); on the global
   * calendar it is immediate ("Delete post"). The handler is what actually
   * differs; this only keeps the wording honest about that.
   */
  removal: "staged" | "immediate";
};

const CalendarPostPopoverContent: FC<
  {
    /** Snapshot of the currently inspected post. */
    publication: PublicationModel;
    /**
     * The post to show. The selection lives in the host's popover state, so
     * clicking the tile the inspector was opened from after paging lands
     * back on that post instead of being a dead click — which is what the
     * old open-token existed for.
     */
    inspectedPublicationId: string;
    /**
     * The inspector moved to another calendar post. That is all this
     * reports — where the panel sits does not change, and bringing the
     * destination's tile into view is the calendar's job, asked for separately.
     * It hands up no element: this file renders into a portal and has no way
     * to know which period is drawn, where the scrollport is or what is
     * pinned over it.
     */
    onInspectPublication: (publication: PublicationModel) => void;
    /** Live rows from the calendar's own query or staged schedule. */
    publications: PublicationModel[] | undefined;
    now: Date;
    /** The bottom sheet fills its width instead of the desktop's fixed one. */
    compact?: boolean;
    /**
     * The persisted DB row behind an inspected row, or null when the row has
     * never been saved. Identity by default: the global calendar only ever
     * inspects real rows.
     *
     * The scheduler's calendar does not. It draws the PENDING plan, so its
     * tiles are preview rows keyed `bulk-<videoId>-<accountId>` whose status is
     * coerced to "scheduled" (a draft has to show a time). Those ids address
     * nothing on the server, and the coerced status hides that fact — so
     * anything that reads or writes BY ID goes through this, and the menu is
     * told whether a row exists at all.
     */
    resolvePersistedPublication?: (
      publication: PublicationModel,
    ) => PublicationModel | null;
    /** Same per-row condition as the schedule calendar's saved-post icon. */
    showSavedScheduledStatus?: (publication: PublicationModel) => boolean;
  } & CalendarPostActions
> = ({
  publication,
  inspectedPublicationId,
  publications,
  now,
  compact = false,
  resolvePersistedPublication = (candidate) => candidate,
  showSavedScheduledStatus,
  onEditPost,
  openScheduleLabel,
  onEditVideo,
  onOpenProject,
  onManageAutoPost,
  onViewAnalytics,
  onPostNow,
  onDelete,
  removal,
  onInspectPublication,
}) => {
  const inspected = publications?.find((post) => post.id === inspectedPublicationId)
    ?? publication;
  const videoId = inspected.projectId;
  const navigationPublications = useMemo(
    () => selectCalendarNavigationPublications(publications, videoId),
    [publications, videoId],
  );
  const { data: video, isLoading: isVideoLoading } = useVideo(videoId);
  const {
    readAccounts: { data: accounts },
  } = useSocialProviderAccounts();

  const account = accounts?.find(
    (candidate) => candidate.id === inspected.accountId,
  );

  // `inspected` is what the user is LOOKING at — the row that owns the tile,
  // the caption and the pager. `canonical` is what the post IS on the server.
  // They are the same object everywhere except the scheduler's calendar, where
  // the inspected row is a preview of an edit that has not been saved yet.
  // Read/write-by-id goes through `canonical`; identity and display stay with
  // `inspected`, so a caption typed a second ago is the one on screen.
  const persisted = resolvePersistedPublication(inspected);
  const canonical = persisted ?? inspected;
  const isPersisted = persisted !== null;
  const statusKind = usePublicationStatusKind({
    status: canonical.status,
    scheduledAt: isPersisted ? canonical.scheduledAt : undefined,
  });
  const statusBadge = isPersisted ? selectPublicationStatusBadge({
    status: canonical.status,
    statusKind,
    showScheduledBadge: Boolean(showSavedScheduledStatus?.(inspected)),
  }) : null;

  const isLive = canonical.status === "published";
  const canViewAnalytics = isLive && isPersisted;

  const videoState: CalendarPostMenuScope["videoState"] = !videoId
    ? "missing"
    : isVideoLoading
      ? "loading"
      : video
        ? "ready"
        : "missing";
  const folderId = video?.folderId ?? null;
  // Both folder-owned and standalone shorts have a scheduling workspace.
  const canOpenSchedule = videoState === "ready";
  const menuScope: CalendarPostMenuScope = {
    openScheduleLabel,
    videoState,
    hasProject: folderId !== null,
    hasAccount: Boolean(account),
    isPersisted,
    removal,
  };
  const items = buildCalendarPostMenuItems(canonical, now, menuScope);
  const canRetry = canOfferCalendarRetry(canonical, now, menuScope);
  const errorMessage = getPublicationErrorMessage(canonical);

  // Each destination supplies its own caption, status and video.
  const caption =
    inspected.provider && inspected.postInfo
      ? PublicationHelper.getTitleFromProvider(
          inspected.provider,
          inspected.postInfo,
        )
      : undefined;

  const runAction = (id: CalendarPostMenuItemId) => {
    switch (id) {
      case "view-live":
        if (canonical.publishedUrl) {
          window.open(canonical.publishedUrl, "_blank", "noopener,noreferrer");
        }
        return;
      case "edit-video":
        if (video) onEditVideo(video);
        return;
      case "open-project":
        if (folderId) onOpenProject(folderId);
        return;
      case "edit-post":
      case "open-schedule":
        // Keep the visible row's identity, including unsaved scheduler edits.
        if (canOpenSchedule) onEditPost(inspected, folderId);
        return;
      case "post-now":
        // The menu already withholds this without a persisted row; the guard
        // stays so no future gating change can leak a preview id into a write.
        if (video && account && persisted) onPostNow(video, persisted, account);
        return;
      case "delete":
        // The scheduler resolves the inspected preview ID back to its local
        // publication ID; this also works for never-saved drafts.
        onDelete(inspected, account ?? null);
        return;
    }
  };

  const navigation = navigationPublications.length > 1 ? (
    <CalendarPostNav
      previous={stepCalendarPublication(navigationPublications, inspected.id, -1)}
      next={stepCalendarPublication(navigationPublications, inspected.id, 1)}
      index={navigationPublications.findIndex((post) => post.id === inspected.id)}
      count={navigationPublications.length}
      onNavigate={onInspectPublication}
    />
  ) : null;

  // Display, like the tile: in the scheduler a staged time or status change
  // already makes the post the user's own (withStagedAutoFolderId).
  const autoFolderId = inspected.autoFolderId;
  const postStatus = (statusBadge && !errorMessage) || autoFolderId ? (
    <div className="flex flex-wrap items-center gap-1">
      {statusBadge && !errorMessage ? (
        <CalendarPostStatusBadge
          kind={statusBadge.kind}
          scheduledAt={canonical.scheduledAt}
        />
      ) : null}
      <AutoPostPublicationMark
        autoFolderId={autoFolderId}
        onManage={onManageAutoPost && autoFolderId ? () => onManageAutoPost(autoFolderId) : undefined}
        showLabel
        className="pointer-events-auto rounded-full bg-white/95 px-2 py-0.5 text-slate-900 shadow-sm [&_svg]:text-primary"
      />
    </div>
  ) : null;

  const actionBar = (
    <div data-calendar-post-actions className="flex w-full items-center gap-2">
      {navigation}
      <div className="ml-auto flex shrink-0 items-center gap-0">
        {canViewAnalytics ? (
          <Button
            type="button"
            variant="ghost"
            size="s"
            aria-label="View analytics"
            data-tooltip-id="global"
            data-tooltip-content="View analytics"
            onClick={() => onViewAnalytics(canonical)}
          >
            <ChartBarIcon />
          </Button>
        ) : null}
        {isLive && canonical.publishedUrl ? (
          <Button
            type="button"
            variant="ghost"
            size="s"
            aria-label="View live"
            data-tooltip-id="global"
            data-tooltip-content="View live"
            onClick={() => runAction("view-live")}
          >
            <ArrowUpRightIcon />
          </Button>
        ) : null}
        {items.length > 0 ? (
          <PublicationCardCornerMenu
            key={inspected.id}
            ariaLabel="Post actions"
            reveal="always"
            tone="media"
          >
            {({ close }) => (
              <ul>
                {items.map((item) => (
                  <li key={item.id}>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="m"
                      align="start"
                      disabled={item.disabled}
                      className="w-full"
                      onClick={() => {
                        close();
                        runAction(item.id);
                      }}
                    >
                      {MENU_ITEM_ICON[item.id]}
                      {item.label}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </PublicationCardCornerMenu>
        ) : null}
      </div>
    </div>
  );

  const failureNotice = errorMessage ? (
    <div
      role="status"
      className="max-h-40 overflow-y-auto border-t border-border p-3"
    >
      <div className="flex items-center gap-1.5 text-destructive">
        <ExclamationCircleIcon className="h-4 w-4 shrink-0" aria-hidden />
        <Typography variant="subtitle3">Publishing failed</Typography>
      </div>
      <Typography variant="caption" className="mt-1 text-foreground-secondary break-words">
        {errorMessage}
      </Typography>
      {canRetry ? (
        <Button
          type="button"
          variant="secondary"
          size="s"
          className="mt-2"
          onClick={() => {
            if (video && account && persisted) onPostNow(video, persisted, account);
          }}
        >
          <PaperAirplaneIcon />
          Retry now
        </Button>
      ) : null}
    </div>
  ) : null;

  // Reserve the notice's maximum height (160px) before fitting the media to
  // the viewport. On mobile, the 6rem allowance also clears the close button.
  const ratioW = video?.width || 9;
  const ratioH = video?.height || 16;
  const reservedHeight = errorMessage ? 160 : 0;
  const frameWidth = `min(${compact ? "100%" : "240px"}, calc((100dvh - ${compact ? "6rem" : "2rem"} - ${reservedHeight}px) * ${ratioW} / ${ratioH}))`;

  return (
    <div
      data-calendar-post-inspector
      className={twMerge(
        "overflow-hidden rounded-xl bg-background text-foreground ring-1 ring-border dark:bg-surface",
        compact && "mx-auto",
      )}
      style={{ width: frameWidth }}
    >
      {videoId ? (
        <CalendarPostPlayer
          videoId={videoId}
          video={video ?? null}
          title={caption}
          statusBadge={postStatus}
          actions={actionBar}
          width="100%"
        />
      ) : (
        <div className="dark relative flex aspect-[9/16] items-center justify-center bg-slate-950 p-6 text-center">
          <Typography variant="body2" className="text-white/70">
            This short is no longer available.
          </Typography>
          <div className="absolute inset-x-0 top-0 flex items-center p-2">
            {actionBar}
          </div>
          {postStatus ? (
            <div className="absolute inset-x-0 bottom-0 p-2">{postStatus}</div>
          ) : null}
        </div>
      )}
      {failureNotice}
    </div>
  );
};

export default CalendarPostPopoverContent;
